import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { catalog } from "./api"

export function ManualDocumentUpload({ companyId }: { companyId: string }) {
  const { hasPermission } = useUser()
  const { translate: t } = useTranslation()
  const cache = useQueryClient()
  const [open, setOpen] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [source, setSource] = useState("")
  const [page, setPage] = useState("")
  const [key, setKey] = useState(() => crypto.randomUUID())
  const save = useMutation({
    mutationFn: () => catalog.upload(companyId, file!, source, page, key),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ["company-index"] })
    },
  })
  const edit = () => {
    setKey(crypto.randomUUID())
    save.reset()
  }
  if (!hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD)) return null
  return (
    <section className="space-y-3 rounded-xl border border-border p-4">
      <Button
        variant="outline"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {t("CI_UPLOAD_DOCUMENT")}
      </Button>
      {open && (
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault()
            save.mutate()
          }}
        >
          <p className="text-sm text-muted-foreground">{t("CI_UPLOAD_HINT")}</p>
          <label className="block">
            {t("CI_UPLOAD_FILE")}
            <Input
              aria-label={t("CI_UPLOAD_FILE")}
              type="file"
              accept=".pdf,.xlsx,.xlsm"
              disabled={save.isPending}
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null)
                edit()
              }}
            />
          </label>
          <label className="block">
            {t("CI_SOURCE")}
            <Input
              aria-label={t("CI_SOURCE")}
              type="url"
              pattern="https?://.*"
              required
              maxLength={4096}
              value={source}
              disabled={save.isPending}
              onChange={(event) => {
                setSource(event.target.value)
                edit()
              }}
            />
          </label>
          <label className="block">
            {t("CI_PRODUCT_PAGE")}
            <Input
              aria-label={t("CI_PRODUCT_PAGE")}
              type="url"
              pattern="https?://.*"
              required
              maxLength={4096}
              value={page}
              disabled={save.isPending}
              onChange={(event) => {
                setPage(event.target.value)
                edit()
              }}
            />
          </label>
          {file && file.size > 20 * 1024 * 1024 && (
            <p role="alert">{t("CI_UPLOAD_SIZE")}</p>
          )}
          <Button
            type="submit"
            disabled={
              save.isPending ||
              save.isSuccess ||
              !file ||
              file.size > 20 * 1024 * 1024 ||
              !source.trim() ||
              !page.trim()
            }
          >
            {t("CI_UPLOAD_SAVE")}
          </Button>
          {save.isError && <p role="alert">{t("CI_ERROR")}</p>}
          {save.isSuccess && <p role="status">{t("CI_UPLOAD_SAVED")}</p>}
        </form>
      )}
    </section>
  )
}
